import { GQL } from "@binale-tech/shared";

import { isInYear } from "./inYear";

/** The server's maximum of document or transaction ids per request. */
export const MAX_IDS_PER_JOB = 500;

export interface DraftDocument {
    id: string;
    type: string;
    subType?: string | null;
}

export interface DraftJob {
    productKey: GQL.IProductKey;
    groupId?: string;
    documentIds: string[];
}

export interface DraftPlan {
    jobs: DraftJob[];
    /** Bank and Kasse documents whose folder is bound to no accounting group; the server would refuse them. */
    withoutGroup: number;
    /** The ids behind `withoutGroup`. */
    withoutGroupIds: string[];
}

const PRODUCT_ORDER = [GQL.IProductKey.Er, GQL.IProductKey.Bank, GQL.IProductKey.Kb];

/** One job per product and group (ER has none), at most MAX_IDS_PER_JOB documents each. */
export const planDraftJobs = (
    documents: DraftDocument[],
    groupIdOf: (type: string, subType: string | undefined) => string | undefined,
): DraftPlan => {
    const buckets = new Map<string, DraftJob>();
    const seen = new Set<string>();
    const withoutGroupIds: string[] = [];
    for (const doc of documents) {
        const productKey = PRODUCT_ORDER.find((p) => p === doc.type);
        if (!productKey || seen.has(doc.id)) continue;
        seen.add(doc.id);
        const needsGroup = productKey !== GQL.IProductKey.Er;
        const groupId = needsGroup ? groupIdOf(doc.type, doc.subType ?? undefined) : undefined;
        if (needsGroup && !groupId) {
            withoutGroupIds.push(doc.id);
            continue;
        }
        const key = `${productKey}|${groupId ?? ""}`;
        const bucket = buckets.get(key) ?? { productKey, groupId, documentIds: [] };
        bucket.documentIds.push(doc.id);
        buckets.set(key, bucket);
    }
    const jobs = [...buckets.values()]
        .sort((a, b) => PRODUCT_ORDER.indexOf(a.productKey) - PRODUCT_ORDER.indexOf(b.productKey))
        .flatMap((bucket) =>
            Array.from({ length: Math.ceil(bucket.documentIds.length / MAX_IDS_PER_JOB) }, (_, i) => ({
                ...bucket,
                documentIds: bucket.documentIds.slice(i * MAX_IDS_PER_JOB, (i + 1) * MAX_IDS_PER_JOB),
            })),
        );
    return { jobs, withoutGroup: withoutGroupIds.length, withoutGroupIds };
};

/** Ids of the year's transactions that have no draft yet. */
export const selectUnexported = (
    transactions: { id: string; date: string | null | undefined }[],
    exportedIds: ReadonlySet<string>,
    year: number,
): string[] => transactions.filter((tx) => isInYear(tx.date, year) && !exportedIds.has(tx.id)).map((tx) => tx.id);
